import { useMemo } from "react";

import { Alert, Stack, Typography } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";

import { EdictEntryResponse } from "@/api";
import { DataTable } from "@/common/components/table/DataTable";
import { EdictRow } from "@/pages/edicts/Edicts.types";
import { useTranslation } from "@/translations";

import { useEdictsColumns } from "./hooks/useEdictsColumns";

const EdictRowHeight = 56;

interface Props {
  entries: EdictEntryResponse[];
  isFetching: boolean;
  isError: boolean;
  hasSearched: boolean;
  isFilteredEmpty: boolean;
  paginationResetKey: string;
  showCityColumn: boolean;
  isEntrySelectable: (entry: EdictEntryResponse) => boolean;
  selectionModel: GridRowSelectionModel;
  onSelectionModelChange: (model: GridRowSelectionModel) => void;
  onEntryClick: (entry: EdictRow) => void;
}

export function EdictsResults({
  entries,
  isFetching,
  isError,
  hasSearched,
  isFilteredEmpty,
  paginationResetKey,
  showCityColumn,
  isEntrySelectable,
  selectionModel,
  onSelectionModelChange,
  onEntryClick,
}: Props) {
  const { t } = useTranslation();
  const columns = useEdictsColumns(showCityColumn);

  const rows = useMemo<EdictRow[]>(() => entries.map((entry, index) => ({ ...entry, rowId: index })), [entries]);

  // A failed search must never fall through to the empty state — a count of zero would read as
  // "nothing was published in this period" instead of "the register could not be reached".
  const showEmptyState = !isError && hasSearched && entries.length === 0 && !isFetching;

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      {isError && <Alert severity="error">{t("edicts.results.error")}</Alert>}

      {showEmptyState && (
        <Typography color="text.secondary">
          {t(isFilteredEmpty ? "edicts.results.emptyFiltered" : "edicts.results.empty")}
        </Typography>
      )}

      {!isError && !showEmptyState && (
        <Stack sx={{ overflow: "auto", flex: 1, minHeight: 0 }}>
          <DataTable<EdictRow>
            data={rows}
            columns={columns}
            getRowId={(row) => row.rowId.toString()}
            // The whole row opens the entry's details, as everywhere else in the application. The links
            // inside the cells stop the event themselves, so they still go to their own target.
            onRowClick={(rowId) => {
              const row = rows.find((entry) => entry.rowId.toString() === rowId);
              if (row) {
                onEntryClick(row);
              }
            }}
            isLoading={isFetching}
            rowHeight={EdictRowHeight}
            disableColumnMenu
            checkboxSelection
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={onSelectionModelChange}
            // Only an entry the register's action can actually process carries a checkbox, so it is also
            // left out when the header selects everything.
            isRowSelectable={(params) => isEntrySelectable(params.row)}
            paginationResetKey={paginationResetKey}
          />
        </Stack>
      )}
    </Stack>
  );
}
